import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useEffect } from 'react';

const BenefitsSection = ({ setActiveSection }: BenefitsSectionProps) => {
  const { ref, inView } = useInView({ 
    threshold: 0.2,
    triggerOnce: false 
  });

  useEffect(() => {
    if (inView) {
      setActiveSection('benefits');
    }
  }, [inView, setActiveSection]);

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Fast Turnaround',
      description: 'AI-powered workflows deliver results in record time'
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost Effective',
      description: 'Premium quality without premium agency prices'
    },
    {
      icon: 'ri-infinity-line',
      title: 'Unlimited Revisions',
      description: 'Perfect your content until it matches your vision'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Quality Guaranteed',
      description: 'Every project meets our high standards'
    }
  ];

  return (
    <section
      id="benefits"
      ref={ref}
      className="relative py-16 sm:py-20 lg:py-32 px-4 sm:px-6 lg:px-12 bg-black"
    >
      <div className="max-w-[1600px] mx-auto">
        {/* Title */}
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-xs sm:text-sm uppercase tracking-[3px] text-brand-aqua mb-3 font-semibold">
            WHY CHOOSE US
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight px-4">
            Benefits
          </h2>
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/10 hover:border-brand-pink/50 transition-all duration-300"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center bg-gradient-to-br from-brand-pink/20 to-brand-aqua/20 rounded-2xl mb-5 sm:mb-6">
                <i className={`${benefit.icon} text-3xl sm:text-4xl text-brand-aqua`}></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 leading-tight">
                {benefit.title}
              </h3>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
